// ConsoleView.js
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Button,
  Switch,
  Typography,
  Spin,
  Empty,
  Tag,
  Tooltip,
  Space,
  Divider,
  Badge,
} from "antd";
import {
  DownOutlined,
  ClearOutlined,
  CodeOutlined,
  LoadingOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const MAX_STORED_LOGS = 500; // Maximale Anzahl der zu speichernden Logs

const ConsoleView = () => {
  const [logs, setLogs] = useState([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const consoleEndRef = useRef(null);
  const eventSourceRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [stats, setStats] = useState({ error: 0, warn: 0, info: 0, log: 0 });

  // Load saved logs from localStorage on mount
  useEffect(() => {
    try {
      const savedLogs = localStorage.getItem("console_logs");
      if (savedLogs) {
        const parsedLogs = JSON.parse(savedLogs);
        setLogs(parsedLogs);

        // Rebuild stats from saved logs
        const newStats = { error: 0, warn: 0, info: 0, log: 0 };
        parsedLogs.forEach((log) => {
          if (log.type && newStats[log.type] !== undefined) {
            newStats[log.type]++;
          }
        });
        setStats(newStats);
      }
      setIsVisible(true);
    } catch (err) {
      console.error("Error loading saved logs:", err);
      setIsVisible(true);
    }
  }, []);

  // Save logs to localStorage when updated
  useEffect(() => {
    // Only store up to MAX_STORED_LOGS to prevent localStorage from getting too large
    const logsToStore = logs.slice(-MAX_STORED_LOGS);
    localStorage.setItem("console_logs", JSON.stringify(logsToStore));
  }, [logs]);

  useEffect(() => {
    // Verbindung zum SSE-Stream herstellen
    const connectToConsole = () => {
      try {
        const eventSource = new EventSource(
          `${process.env.REACT_APP_API_URL}/api/console-stream`,
          { withCredentials: true }
        );
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          setConnected(true);
          setError(null);
        };

        eventSource.addEventListener("connected", (event) => {
          const data = JSON.parse(event.data);
          console.log(
            "Connected to console stream with clientId:",
            data.clientId
          );
        });

        eventSource.addEventListener("console", (event) => {
          const logData = JSON.parse(event.data);
          setLogs((prevLogs) => {
            const newLogs = [
              ...prevLogs,
              {
                id: Date.now() + Math.random(),
                timestamp: new Date().toISOString(),
                ...logData,
              },
            ];

            // Store the updated logs in localStorage
            const logsToStore = newLogs.slice(-MAX_STORED_LOGS);
            localStorage.setItem("console_logs", JSON.stringify(logsToStore));

            // Aktualisiere die Statistiken
            setStats((prevStats) => {
              const newStats = { ...prevStats };
              newStats[logData.type] = (newStats[logData.type] || 0) + 1;
              return newStats;
            });

            return newLogs;
          });
        });

        eventSource.onerror = (err) => {
          console.error("SSE Error:", err);
          setError(
            "Verbindung zur Serverkonsole unterbrochen. Versuche erneut zu verbinden..."
          );
          setConnected(false);
          eventSource.close();

          // Reconnect nach Timeout
          setTimeout(connectToConsole, 5000);
        };
      } catch (err) {
        setError(`Fehler beim Verbinden: ${err.message}`);
        setTimeout(connectToConsole, 5000);
      }
    };

    connectToConsole();

    // Cleanup beim Unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Logs löschen
  const clearLogs = () => {
    setLogs([]);
    setStats({ error: 0, warn: 0, info: 0, log: 0 });
    localStorage.removeItem("console_logs");
  };

  // Log-Level-Icon
  const getLogIcon = (type) => {
    switch (type) {
      case "error":
        return <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />;
      case "warn":
        return <WarningOutlined style={{ color: "#faad14" }} />;
      case "info":
        return <InfoCircleOutlined style={{ color: "#1890ff" }} />;
      case "log":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      default:
        return <CodeOutlined style={{ color: "#d9d9d9" }} />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  if (!isVisible) return null;

  return (
    <Card
      className="console-card"
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <CodeOutlined />
            <span>Server-Konsole</span>
            {connected ? (
              <Badge status="success" text="Verbunden" />
            ) : (
              <Badge status="error" text="Getrennt" />
            )}
          </Space>
          <Space>
            <Space size="small" split={<Divider type="vertical" />}>
              <Tooltip title="Fehler">
                <Badge
                  count={stats.error}
                  style={{ backgroundColor: "#ff4d4f" }}
                  showZero
                />
              </Tooltip>
              <Tooltip title="Warnungen">
                <Badge
                  count={stats.warn}
                  style={{ backgroundColor: "#faad14" }}
                  showZero
                />
              </Tooltip>
              <Tooltip title="Infos">
                <Badge
                  count={stats.info}
                  style={{ backgroundColor: "#1890ff" }}
                  showZero
                />
              </Tooltip>
              <Tooltip title="Logs">
                <Badge
                  count={stats.log}
                  style={{ backgroundColor: "#52c41a" }}
                  showZero
                />
              </Tooltip>
            </Space>
            <Tooltip title="Zeitstempel anzeigen">
              <Switch
                checked={showTimestamps}
                onChange={setShowTimestamps}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Automatisches Scrollen">
              <Switch
                checked={autoScroll}
                onChange={setAutoScroll}
                size="small"
              />
            </Tooltip>
            <Tooltip title="Konsole leeren">
              <Button
                icon={<ClearOutlined />}
                onClick={clearLogs}
                size="small"
              />
            </Tooltip>
            <Tooltip title={expanded ? "Minimieren" : "Maximieren"}>
              <Button
                icon={<DownOutlined rotate={expanded ? 180 : 0} />}
                onClick={() => setExpanded(!expanded)}
                size="small"
              />
            </Tooltip>
          </Space>
        </div>
      }
      style={{ marginBottom: 16 }}
      bodyStyle={{
        padding: 0,
        height: expanded ? "400px" : "200px",
        transition: "height 0.3s ease",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {error && (
        <div
          style={{
            padding: "8px 16px",
            backgroundColor: "#fff2f0",
            borderBottom: "1px solid #ffccc7",
            color: "#ff4d4f",
          }}
        >
          <ExclamationCircleOutlined style={{ marginRight: 8 }} />
          {error}
        </div>
      )}

      <div
        style={{
          height: "100%",
          overflowY: "auto",
          padding: "12px 16px",
          backgroundColor: "#fafafa",
        }}
      >
        {logs.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Keine Konsolenausgaben"
            style={{ margin: "40px 0" }}
          />
        ) : (
          logs.map((log) => (
            <div
              key={log.id}
              style={{
                marginBottom: 8,
                display: "flex",
                alignItems: "flex-start",
                lineHeight: 1.5,
                color:
                  log.type === "error"
                    ? "#ff4d4f"
                    : log.type === "warn"
                    ? "#fa8c16"
                    : log.type === "info"
                    ? "#1890ff"
                    : "rgba(0, 0, 0, 0.65)",
              }}
            >
              {showTimestamps && (
                <Text
                  type="secondary"
                  style={{ marginRight: 8, fontSize: 12, whiteSpace: "nowrap" }}
                >
                  {formatTimestamp(log.timestamp)}
                </Text>
              )}
              <span style={{ marginRight: 8, marginTop: 3 }}>
                {getLogIcon(log.type)}
              </span>
              <div
                style={{
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                  fontFamily:
                    "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
                  fontSize: 13,
                  flex: 1,
                }}
              >
                {log.message}
              </div>
            </div>
          ))
        )}
        <div ref={consoleEndRef} />
      </div>

      {!connected && (
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: 16,
            backgroundColor: "rgba(0, 0, 0, 0.03)",
            borderTop: "1px solid #f0f0f0",
            textAlign: "center",
          }}
        >
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
            tip="Verbindung wird hergestellt..."
          />
        </div>
      )}
    </Card>
  );
};

export default ConsoleView;
